/**
 * useTutorLivePresentation — « Présenter » et le partage d'écran de la barre, dans la salle du tuteur.
 *
 * Le port de `common/liveMode/liveV3/ScreenShareToggleButton{,.web}.js` (« Présenter ») et du partage
 * de `common/header/LiveModeFooter.js` (`onScreenSharePress`), sur le partage du moteur
 * (`engine/live/room/usePresentationShare`) :
 *  - l'état de la présentation est **le même état Redux** que l'ancien live
 *    (`liveMode.isFocusScreenShare`, `setFocusScreenShare`) : le centre (la correction tue), les gardes
 *    « Interrompre la présentation ? » et l'ancien écran le lisent pareil ;
 *  - web : recadré sur le centre (`shareRegionRef`) quand le navigateur sait (`CropTarget`) et que la
 *    région se recadre (la règle du moteur, `captureOf`), sinon l'onglet entier — et alors la page se
 *    réduit au centre (`centerOnly`) dès le départ, avant que la piste ne parte, et tant que la présentation
 *    dure, pour qu'aucun prénom, aucune vidéo ni le fil ne partent chez les élèves (le
 *    tech-derisk du câblage, point 3) ; le bouton de la barre partage l'onglet sans toucher à « Présenter », comme l'ancienne
 *    barre ;
 *  - natif : « Présenter » et la barre sont le même partage de l'écran entier, qui bascule
 *    « Présenter » (l'ancien `LiveModeFooter.js:244-248`) : `centerOnly` aussi ;
 *  - le partage qui s'arrête ailleurs (le bouton du navigateur, le centre de contrôle iOS, le bouton de
 *    la barre, une piste iOS cassée) fait finir la présentation ; une présentation arrêtée ailleurs
 *    (`setFocusScreenShare(false)` d'une garde) coupe le partage, sur la seule transition vrai → faux.
 *
 * Aucun appel d'API, aucun message du canal de données : `PRESENTATION_EQUIVALENCE` le tient, geste par
 * geste.
 */
import type React from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { type View } from "react-native"
import { useDispatch, useSelector } from "react-redux"

import { setFocusScreenShare } from "../../../../../../data/redux/actions/liveMode"
import { isScreenSharePublished, type PresentationCapture } from "../../../../../../engine/live/room/presentationShare"
import { usePresentationShare } from "../../../../../../engine/live/room/usePresentationShare"
import { IS_WEB } from "../../../../../../engine/shared/platform"
import { type TutorLivePresentation, type TutorLiveRoomContext } from "./tutorLiveContract"

export type { PresentationCapture } from "../../../../../../engine/live/room/presentationShare"

/**
 * La page se réduit au centre quand ce que voient les élèves n'est pas recadré : pendant la présentation
 * d'un onglet entier ou d'un écran natif, et déjà pendant son départ (le sélecteur ouvert), pour que la
 * première image partagée soit déjà le centre seul. Un partage recadré garde la page entière.
 */
export function presentationCenterOnly(args: {
    on: boolean
    cropped: boolean
    starting: PresentationCapture | null
}): boolean {
    if (args.on) return !args.cropped
    return args.starting === "full"
}

/**
 * Le protocole de chaque geste, face à l'ancien live : ni service d'API, ni message de salle ; une
 * opération sur les pistes LiveKit et l'état Redux `isFocusScreenShare`. Un test par ligne.
 */
export const PRESENTATION_EQUIVALENCE = [
    {
        gesture: "present_cropped",
        legacy: "ScreenShareToggleButton.web.js handleToggle (CropTarget)",
        service: null,
        message: null,
        livekit:
            'getDisplayMedia({ video: { displaySurface: "browser" }, preferCurrentTab: true }) → cropTo → publishTrack(LocalVideoTrack, { source: ScreenShare, name: "focus-screen-share" })',
        redux: "setFocusScreenShare(true)",
    },
    {
        gesture: "present_full_tab",
        legacy: "ScreenShareToggleButton.web.js handleToggle (sans CropTarget)",
        service: null,
        message: null,
        livekit: 'setScreenShareEnabled(true, { audio: true, preferCurrentTab: true, selfBrowserSurface: "include" })',
        redux: "setFocusScreenShare(true)",
    },
    {
        gesture: "stop_cropped",
        legacy: "ScreenShareToggleButton.web.js handleToggle (arrêt, activeCropTrack)",
        service: null,
        message: null,
        livekit: "track.stop() → unpublishTrack(track)",
        redux: "setFocusScreenShare(false)",
    },
    {
        gesture: "stop_full_tab",
        legacy: "ScreenShareToggleButton.web.js handleToggle (arrêt)",
        service: null,
        message: null,
        livekit: "setScreenShareEnabled(false)",
        redux: "setFocusScreenShare(false)",
    },
    {
        gesture: "browser_stop",
        legacy: "ScreenShareToggleButton.web.js videoTrack.onended",
        service: null,
        message: null,
        livekit: "unpublishTrack(track)",
        redux: "setFocusScreenShare(false)",
    },
    {
        gesture: "external_stop",
        legacy: "ScreenShareToggleButton.web.js, effet sur isFocusScreenShare vrai → faux",
        service: null,
        message: null,
        livekit: "setScreenShareEnabled(false) (ou la piste recadrée dépubliée)",
        redux: "— (déjà à faux)",
    },
    {
        gesture: "bar_share_web",
        legacy: "ControlBarM.web.tsx TrackToggleM / useScreenShare.web.js",
        service: null,
        message: null,
        livekit:
            'useTrackToggle({ source: ScreenShare, captureOptions: { audio: true, selfBrowserSurface: "include" } })',
        redux: "—",
    },
    {
        gesture: "present_native",
        legacy: "ScreenShareToggleButton.js + useScreenShare.js toggleScreenShare",
        service: null,
        message: null,
        livekit: "ScreenCapturePickerViewManager.show(tag) (iOS) → setScreenShareEnabled(true)",
        redux: "setFocusScreenShare(true)",
    },
    {
        gesture: "bar_share_native",
        legacy: "LiveModeFooter.js onScreenSharePress",
        service: null,
        message: null,
        livekit: "comme present_native ; l'arrêt : unpublishTrack(pub.track, false)",
        redux: "setFocusScreenShare(true / false)",
    },
] as const

type RootState = { liveMode?: { isFocusScreenShare?: boolean } }

export function useTutorLivePresentation(room: TutorLiveRoomContext): TutorLivePresentation {
    const dispatch = useDispatch()
    const on = useSelector((state: RootState) => state?.liveMode?.isFocusScreenShare === true)
    const liveRoom = room.live?.room
    const share = usePresentationShare(liveRoom, room.telemetry)
    const shareRegionRef = useRef<View>(null)
    const [starting, setStarting] = useState<PresentationCapture | null>(null)
    // La dernière valeur que ce crochet a écrite : une présentation qu'il arrête lui-même ne se ré-arrête pas.
    const ownFocusRef = useRef<boolean | null>(null)

    const setFocus = useCallback(
        (value: boolean) => {
            ownFocusRef.current = value
            dispatch(setFocusScreenShare(value) as never)
        },
        [dispatch]
    )

    // Le partage n'est plus là (arrêté ailleurs, piste iOS cassée retirée, état d'avant un rechargement) :
    // la présentation finit. Lu sur la salle elle-même, pas sur l'état rendu, pour ne pas la finir entre
    // la publication et son événement.
    useEffect(() => {
        if (on && !share.pending && !isScreenSharePublished(liveRoom)) setFocus(false)
    }, [on, share.sharing, share.pending, liveRoom, setFocus])

    // La présentation arrêtée ailleurs (une garde écrit `setFocusScreenShare(false)`) : le partage s'arrête,
    // sur la seule transition vrai → faux (un remontage ne coupe pas un partage de la barre).
    const previousOnRef = useRef(on)
    const stopShare = share.stop
    useEffect(() => {
        const was = previousOnRef.current
        previousOnRef.current = on
        if (!was || on) return
        const own = ownFocusRef.current === false
        ownFocusRef.current = null
        if (own || share.pending || !isScreenSharePublished(liveRoom)) return
        void stopShare()
    }, [on, share.pending, liveRoom, stopShare])

    const startShare = share.start
    const begin = useCallback(
        (capture: PresentationCapture) => {
            setStarting(capture)
            void startShare(shareRegionRef.current).then((started) => {
                setStarting(null)
                if (started) setFocus(true)
            })
        },
        [startShare, setFocus]
    )

    // Ce qui partira, lu par la règle même du partage (l'ancien `isCropSupported && cropTargetRef?.current`,
    // plus « la région est un élément à recadrer ») : une région qui ne se recadre pas met la page en
    // centre seul avant que la piste ne parte.
    const captureOf = share.captureOf
    const start = useCallback(() => {
        if (on || share.pending || starting != null) return
        begin(captureOf(shareRegionRef.current))
    }, [on, share.pending, captureOf, starting, begin])

    const stop = useCallback(() => {
        if (share.pending) return
        void stopShare().then((stopped) => {
            if (stopped) setFocus(false)
        })
    }, [share.pending, stopShare, setFocus])

    // Au natif, la barre partage comme « Présenter » et le bascule (l'ancien `onScreenSharePress`).
    const nativeToggle = useCallback(() => {
        if (share.pending || starting != null) return
        if (share.sharing) stop()
        else begin("full")
    }, [share.pending, share.sharing, starting, stop, begin])

    const webToggle = share.webToggle
    const screenShare = useMemo(
        () =>
            IS_WEB
                ? webToggle
                : { on: share.sharing, pending: share.pending || starting != null, onToggle: nativeToggle },
        [webToggle, share.sharing, share.pending, starting, nativeToggle]
    )

    return {
        on,
        pending: share.pending || starting != null,
        cropped: on && share.cropped,
        centerOnly: presentationCenterOnly({ on, cropped: share.cropped, starting }),
        shareRegionRef: shareRegionRef as React.RefObject<View>,
        start,
        stop,
        screenShare,
    }
}
